import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Star,
  Baby,
  Shield,
  Clock,
  CheckCircle,
  ArrowRight,
  MapPin,
  Users,
  Car,
  Bus,
  Phone,
  CreditCard,
  Award,
  Zap,
  Heart,
  Plane,
  ThumbsUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import BookingWidget from "../components/booking/BookingWidget";
import Header from "../components/layout/Header";
import city from "../assets/airportHome.webp";
import dtGaredunord5 from "../assets/dtGaredunord5.webp";
import dtParis4 from "../assets/dtParis4.webp";
import dtDisney3 from "../assets/dtDisney3.webp";
import dtOrly2 from "../assets/dtOrly2.webp";
import dtDisney1 from "../assets/dtDisney1.webp";

import dtLadefense6 from "../assets/dtLadefense6.webp";
import heroImage from "../assets/disney.webp";

const FaqAccordion = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: "How much is a CDG to Disneyland transfer?",
      a: "A private CDG to Disneyland transfer starts from €70 per vehicle, depending on the number of passengers and luggage. The journey takes approximately 35–45 minutes with direct hotel drop-off at Disneyland Paris. We offer fixed pricing with no hidden fees. You can pay the driver by card or cash upon arrival.",
    },
    {
      q: "Are child seats really free?",
      a: "Yes! We provide free child seats (infant, toddler, booster) upon request. Just mention your children's ages when booking and we'll have the right seats ready.",
    },
    {
      q: "What if my flight is delayed?",
      a: "We track all flights automatically and adjust pickup times accordingly. There's no extra charge for flight delays - we'll be waiting when you arrive.",
    },
    {
      q: "Can I pay the driver by card?",
      a: "Yes, you can pay by card or cash directly to your driver upon arrival. No online payment required.",
    },
    {
      q: "Where will the driver meet us at CDG Airport?",
      a: "For your CDG to Disneyland transfer, the driver will meet you at the airport arrivals hall with a name sign or at the pick-up point. You will also receive the relevant details, including your driver's name and contact number, before pickup.",
    },
    {
      q: "Is a private transfer better than the train from CDG to Disneyland?",
      a: "A private CDG to Disneyland transfer is the most convenient option, especially for families and travelers with luggage. Unlike the train, a private transfer offers door-to-door service, fixed pricing, and direct drop-off at Disneyland hotels without transfers or waiting times.",
    },
    {
      q: "Do you provide transfers from Orly Airport to Disneyland?",
      a: "Yes. We also offer private transfers from Orly Airport to Disneyland Paris, starting from €80. Your driver will meet you at arrivals and take you directly to your Disneyland hotel.",
    },
    {
      q: "Do you provide transfers from Beauvais Airport to Disneyland?",
      a: "Yes, we also provide private transfers from Beauvais (BVA) Airport to Disneyland Paris, starting from €160 per vehicle. The journey takes approximately 90 minutes depending on traffic.",
    },
  ];

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.05 }}
          className="bg-paris-stone rounded-xl overflow-hidden"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full flex items-center justify-between gap-4 p-6 text-left"
          >
            <span className="font-semibold text-midnight-navy">{faq.q}</span>
            <span
              className="shrink-0 w-6 h-6 bg-champagne-gold/20 rounded-full flex items-center justify-center transition-transform duration-300"
              style={{
                transform:
                  openIndex === index ? "rotate(45deg)" : "rotate(0deg)",
              }}
            >
              <ArrowRight className="w-3 h-3 text-champagne-gold -rotate-45" />
            </span>
          </button>
          {openIndex === index && (
            <div className="px-6 pb-6">
              <p className="text-slate-600 leading-relaxed">{faq.a}</p>
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );
};

const DisneylandTransfers = () => {
  return (
    <div className="min-h-screen" data-testid="disneyland-transfers-page">
      <Header />
      {/* Hero Section - Matching Home Page Structure */}
      <section className="relative min-h-[90vh] lg:min-h-screen flex items-center pt-24 pb-16 lg:pt-32 lg:pb-20">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="Disneyland Paris Castle"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-midnight-navy/90 via-midnight-navy/70 to-midnight-navy/50"></div>
        </div>

        <div className="container mx-auto px-4 lg:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Top Badge */}
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-champagne-gold/20 text-champagne-gold rounded-full text-sm font-medium mb-4 lg:mb-6">
                <Star className="w-4 h-4 fill-current" />
                Trusted by 5,000+ Disneyland Travelers
              </span>

              {/* Headline */}
              <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white leading-tight mb-4 lg:mb-6">
                Private Airport Transfers from{" "}
                <span className="text-champagne-gold">
                  CDG & Orly to Disneyland Paris
                </span>
              </h1>

              {/* Airports + Pricing */}
              <div className="mb-6 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-paris-stone/60 text-xs uppercase tracking-wide font-medium">
                    From
                  </span>
                  {["CDG Airport", "Orly Airport", "Beauvais Airport"].map(
                    (airport) => (
                      <span
                        key={airport}
                        className="flex items-center gap-1 px-3 py-1 bg-white/10 backdrop-blur rounded-full text-white text-xs font-medium"
                      >
                        ✈ {airport}
                      </span>
                    ),
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { route: "CDG → Disneyland", price: "from €70" },
                    { route: "Orly → Disneyland", price: "from €80" },
                  ].map((item) => (
                    <div
                      key={item.route}
                      className="flex items-center gap-2 px-3 py-1.5 bg-champagne-gold/10 border border-champagne-gold/30 rounded-full"
                    >
                      <span className="text-paris-stone/80 text-xs">
                        {item.route}
                      </span>
                      <span className="text-champagne-gold font-bold text-xs">
                        {item.price}
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/20 rounded-full">
                    <Clock className="w-3 h-3 text-paris-stone/60" />
                    <span className="text-paris-stone/80 text-xs">
                      CDG → Disneyland ~40 mins
                    </span>
                  </div>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-2 gap-2 mb-6">
                {[
                  { icon: CheckCircle, label: "Meet & Greet at Airport" },
                  { icon: Plane, label: "Flight Tracking Included" },
                  { icon: Baby, label: "Free Child Seats" },
                  { icon: MapPin, label: "Direct Disney Hotel Drop-Off" },
                ].map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-2 text-white text-sm"
                  >
                    <Icon className="w-4 h-4 text-champagne-gold shrink-0" />
                    <span>{label}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-3 lg:gap-4 mb-6">
                <Button
                  asChild
                  size="lg"
                  className="bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold px-6 py-5 lg:px-8 lg:py-6 rounded-full shadow-lg hover:shadow-xl transition-all text-base lg:text-lg"
                  data-testid="disney-hero-book-btn"
                >
                  <Link to="/booking?dropoff=Disneyland%20Park">
                    Book Disneyland Transfer
                    <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white hover:text-midnight-navy font-semibold px-6 py-5 lg:px-8 lg:py-6 rounded-full transition-all text-base lg:text-lg"
                >
                  <Link to="/pricing">View All Prices</Link>
                </Button>
              </div>

              {/* Bottom Row — Urgency + Phone */}
              <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-white/10">
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1.5 text-green-400">
                    <Zap className="w-4 h-4" />
                    Instant confirmation
                  </span>
                  <span className="flex items-center gap-1.5 text-paris-stone/70">
                    <Clock className="w-4 h-4" />
                    Free cancellations
                  </span>
                </div>
                <a
                  href="tel:+33763519524"
                  className="flex items-center gap-2 text-paris-stone hover:text-champagne-gold transition-colors"
                >
                  <div className="p-2 bg-champagne-gold/20 rounded-full">
                    <Phone className="w-4 h-4 text-champagne-gold" />
                  </div>
                  <div>
                    <p className="text-xs text-paris-stone/70">Call us 24/7</p>
                    <p className="font-semibold text-white text-sm">
                      +33 763 519 524
                    </p>
                  </div>
                </a>
              </div>
            </motion.div>

            {/* Booking Widget - Desktop */}
            <div className="hidden lg:block py-8">
              <BookingWidget
                variant="hero"
                prefilledData={{ dropoff: "Disneyland Park" }}
              />
            </div>
          </div>
        </div>
      </section>
      {/* Mobile Booking Widget */}
      <section className="lg:hidden px-4 py-8 bg-paris-stone relative z-20">
        <BookingWidget
          variant="hero"
          prefilledData={{ dropoff: "Disneyland Park" }}
        />
      </section>
      {/* Social Proof Section */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-midnight-navy">
                4.9/5
              </p>
              <div className="flex gap-1 justify-center my-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-champagne-gold text-champagne-gold"
                  />
                ))}
              </div>
              <p className="text-sm text-slate-600"> Reviews</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-midnight-navy">
                {" "}
                5000+
              </p>
              <p className="text-sm text-slate-600">
                Disneyland Travelers Served
              </p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-midnight-navy">
                99%
              </p>
              <p className="text-sm text-slate-600">On-Time Arrivals</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-midnight-navy">
                24/7
              </p>
              <p className="text-sm text-slate-600">Customer Support</p>
            </div>
          </div>
        </div>
      </section>
      {/* How It Works - 3 Steps */}
      <section className="py-16 md:py-20 bg-paris-stone">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600">
              Book your Disneyland transfer in 3 simple steps
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                step: "1",
                title: "Book Online",
                desc: "Select your route, date & passengers. Get instant confirmation.",
                icon: CheckCircle,
              },
              {
                step: "2",
                title: "Meet Your Driver",
                desc: "Driver waits with your name sign at arrivals. Flight tracking included.",
                icon: MapPin,
              },
              {
                step: "3",
                title: "Relax & Arrive at Disneyland",
                desc: "Relax in a premium vehicle. Pay cash or card to driver on arrival.",
                icon: Heart,
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-champagne-gold rounded-full flex items-center justify-center mx-auto mb-4 text-midnight-navy font-bold text-2xl">
                  {item.step}
                </div>
                <h3 className="font-heading font-bold text-xl text-midnight-navy mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Popular Routes with Prices */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Disneyland Transfer Prices
            </h2>
            <p className="text-lg text-slate-600">
              Fixed prices · No hidden fees · Pay driver on arrival
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                from: "CDG Airport",
                price: 70,
                time: "~40 min",
                popular: true,
                image: dtDisney1,
              },
              {
                from: "Orly Airport",
                price: 80,
                time: "~55 min",
                image: dtOrly2,
              },
              {
                from: "Beauvais Airport",
                price: 155,
                time: "~90 min",
                image: dtDisney3,
              },

              {
                from: "Gare du Nord",
                price: 90,
                time: "~50 min",
                image: dtGaredunord5,
              },
              {
                from: "La Defense",
                price: 110,
                time: "~60 min",
                image: dtLadefense6,
              },

              {
                from: "Paris City Center",
                price: 90,
                time: "~45 min",
                image: dtParis4,
              },
            ].map((route, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/booking?pickup=${encodeURIComponent(
                    route.from,
                  )}&dropoff=Disneyland%20Park`}
                  className={`block rounded-xl border-2 transition-all group overflow-hidden ${
                    route.popular
                      ? "bg-champagne-gold/10 border-champagne-gold hover:bg-champagne-gold/20"
                      : "bg-white border-slate-200 hover:border-champagne-gold"
                  }`}
                >
                  {/* Full Width Top Image */}
                  <div className="w-full overflow-hidden relative">
                    <img
                      src={route.image}
                      alt={`${route.from} to Disneyland Paris`}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  </div>

                  <div className="p-6">
                    {/* Popular Badge */}
                    {route.popular && (
                      <span className="inline-block px-2 py-1 bg-champagne-gold text-midnight-navy text-xs font-bold rounded mb-2">
                        MOST POPULAR TRANSFER
                      </span>
                    )}

                    {/* Text Content */}
                    <div className="flex items-start justify-between mb-4 mt-2">
                      <div>
                        <p className="font-semibold text-midnight-navy">
                          {route.from}
                        </p>
                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                          <ArrowRight className="w-3 h-3" />
                          Disneyland Paris
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-heading font-bold text-2xl text-midnight-navy">
                          €{route.price}
                        </p>
                        <p className="text-xs text-slate-500">{route.time}</p>
                      </div>
                    </div>

                    {/* Button */}
                    <Button className="w-full bg-midnight-navy hover:bg-midnight-navy/90 text-white rounded-full group-hover:bg-champagne-gold group-hover:text-midnight-navy transition-all">
                      Book Now
                    </Button>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Vehicle Showcase */}
      <section className="py-16 md:py-20 bg-paris-stone">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Our Premium Vehicles
            </h2>
            <p className="text-lg text-slate-600">
              Comfortable, spacious & family-friendly
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 shadow-lg"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-eiffel-blue/10 rounded-xl flex items-center justify-center">
                  <Car className="w-8 h-8 text-eiffel-blue" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-midnight-navy">
                    Sedan Car
                  </h3>
                  <div className="flex flex-col gap-1 mt-1">
                    <span className="text-slate-600 text-sm">
                      👥 1–3 passengers
                    </span>
                    <span className="text-slate-600 text-sm">
                      🧳 2 checked + 2 hand luggage
                    </span>
                  </div>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Mercedes
                  E-Class or similar
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Free child
                  seats on request
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Air
                  conditioning & WiFi
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 shadow-lg"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-champagne-gold/10 rounded-xl flex items-center justify-center">
                  <Bus className="w-8 h-8 text-champagne-gold" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-midnight-navy">
                    Spacious Van
                  </h3>
                  <div className="flex flex-col gap-1 mt-1">
                    <span className="text-slate-600 text-sm">
                      👥 Up to 8 passengers
                    </span>
                    <span className="text-slate-600 text-sm">
                      🧳 8 checked + 8 hand luggage
                    </span>
                  </div>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Mercedes
                  V-Class or similar
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Perfect for
                  families & groups
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Extra
                  legroom & comfort
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Luxury Van Upgrade */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-midnight-navy to-slate-900">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            {/* Top Label */}
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-champagne-gold/20 text-champagne-gold rounded-full text-xs font-bold uppercase tracking-widest mb-4">
                <Award className="w-3.5 h-3.5" />
                Optional Upgrade
              </span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mt-3">
                Luxury Van Upgrade
              </h2>
              <p className="text-paris-stone/70 mt-2 text-lg">
                Elevate your Disneyland transfer with a premium experience
              </p>
            </div>

            {/* Card */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 md:p-10 grid md:grid-cols-2 gap-10 items-center">
              {/* Left — Features */}
              <div>
                <p className="text-paris-stone/80 text-base leading-relaxed mb-6">
                  Choose our{" "}
                  <span className="text-white font-semibold">Luxury Van</span>{" "}
                  for added comfort, more space, and a refined travel experience
                  for your Disneyland Paris transfer.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-paris-stone/90 text-sm">
                    <CheckCircle className="w-5 h-5 text-champagne-gold shrink-0" />
                    Spacious interior with extra legroom
                  </li>
                  <li className="flex items-center gap-3 text-paris-stone/90 text-sm">
                    <CheckCircle className="w-5 h-5 text-champagne-gold shrink-0" />
                    Premium seating for a smoother ride
                  </li>
                  <li className="flex items-center gap-3 text-paris-stone/90 text-sm">
                    <CheckCircle className="w-5 h-5 text-champagne-gold shrink-0" />
                    Ideal for families and small groups
                  </li>
                  <li className="flex items-center gap-3 text-paris-stone/90 text-sm">
                    <CheckCircle className="w-5 h-5 text-champagne-gold shrink-0" />
                    Mercedes-Benz V-Class with onboard TV
                  </li>
                  <li className="flex items-center gap-3 text-paris-stone/90 text-sm">
                    <CheckCircle className="w-5 h-5 text-champagne-gold shrink-0" />
                    Porter service included
                  </li>
                  <li className="flex items-center gap-3 text-paris-stone/90 text-sm">
                    <CheckCircle className="w-5 h-5 text-champagne-gold shrink-0" />
                    Ambient starry ceiling lights
                  </li>
                </ul>
              </div>

              {/* Right — Price + CTA */}
              <div className="flex flex-col gap-6">
                {/* Price Callout */}
                <div className="bg-champagne-gold/10 border border-champagne-gold/30 rounded-2xl p-6 text-center">
                  <p className="text-paris-stone/60 text-xs uppercase tracking-widest mb-1">
                    Supplement
                  </p>
                  <p className="text-white text-5xl font-heading font-bold">
                    €10
                  </p>
                  <p className="text-paris-stone/70 text-sm mt-1">
                    per destination
                  </p>
                </div>

                {/* How to request */}
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-sm text-paris-stone/80 leading-relaxed">
                    <span className="font-semibold text-white">
                      How to request:
                    </span>{" "}
                    Mention{" "}
                    <span className="font-semibold text-champagne-gold">
                      "Luxury Van"
                    </span>{" "}
                    in the{" "}
                    <span className="font-semibold text-white">
                      Special Request
                    </span>{" "}
                    field when completing your booking.
                  </p>
                </div>

                {/* CTA */}
                <Button
                  asChild
                  size="lg"
                  className="w-full bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all text-base"
                >
                  <Link
                    to="/booking?dropoff=Disneyland%20Park"
                    className="flex items-center justify-center"
                  >
                    Book with Luxury Van
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Trust Signals */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Why Families Choose Us
            </h2>
            <p className="text-lg text-slate-500 max-w-xl mx-auto">
              Everything you need for a stress-free start to your Disneyland
              adventure
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                icon: Shield,
                title: "Licensed & Insured",
                desc: "All drivers are licensed professionals with full insurance coverage",
                color: "bg-sky-50",
                iconColor: "text-sky-500",
              },
              {
                icon: CreditCard,
                title: "No Hidden Fees",
                desc: "The price you see is the price you pay. All-inclusive fixed pricing",
                color: "bg-emerald-50",
                iconColor: "text-emerald-500",
              },
              {
                icon: Baby,
                title: "Family Friendly",
                desc: "Free child seats (infant, toddler, booster) available on request",
                color: "bg-rose-50",
                iconColor: "text-rose-500",
              },
              {
                icon: Award,
                title: "Premium Service",
                desc: "Professional drivers, clean vehicles, and 24/7 support",
                color: "bg-violet-50",
                iconColor: "text-violet-500",
              },
              {
                icon: Plane,
                title: "Flight Tracking",
                desc: "We monitor your flight and adjust pickup time automatically",
                color: "bg-amber-50",
                iconColor: "text-amber-500",
              },
              {
                icon: Clock,
                title: "24/7 Support",
                desc: "Our team is available around the clock before and during your trip",
                color: "bg-orange-50",
                iconColor: "text-orange-500",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="flex items-start gap-4 p-6 rounded-2xl border border-slate-100 hover:border-champagne-gold/40 hover:shadow-md transition-all group"
              >
                <div
                  className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform`}
                >
                  <item.icon className={`w-6 h-6 ${item.iconColor}`} />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-base text-midnight-navy mb-1">
                    {item.title}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Testimonials */}
      <section className="py-16 md:py-20 bg-paris-stone">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              What Families Say
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                name: "Sarah M. — UK",
                text: "Perfect start to our Disney trip! Driver was waiting at arrivals with a sign, kids loved the spacious van. Will definitely book again!",
                rating: 5,
              },
              {
                name: "James T. — Ireland",
                text: "Best decision we made. After a long flight, having a private transfer straight to the hotel was amazing. Great value for money.",
                rating: 5,
              },
              {
                name: "Emma L. — UK",
                text: "Booked for our family of 5. Free child seats were a lifesaver! Professional driver, clean vehicle. Highly recommend!",
                rating: 5,
              },
            ].map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-soft"
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 fill-champagne-gold text-champagne-gold"
                    />
                  ))}
                </div>
                <p className="text-slate-600 mb-4 italic">"{review.text}"</p>
                <p className="font-semibold text-midnight-navy">
                  {review.name}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-midnight-navy mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <FaqAccordion />
        </div>
      </section>
      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-midnight-navy">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Book Your Disneyland Airport Transfer
            </h2>
            <p className="text-paris-stone/80 mb-6 max-w-xl mx-auto">
              Book your CDG to Disneyland transfer today and start your
              Disneyland Paris adventure stress-free with fixed pricing and
              professional drivers.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold px-8 py-6 rounded-full shadow-lg"
                data-testid="disney-cta-book-btn"
              >
                <Link to="/booking?dropoff=Disneyland%20Park">
                  Book Now - From €70
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white hover:text-midnight-navy font-semibold px-8 py-6 rounded-full"
              >
                <a href="tel:+33763519524">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Us
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Sticky CTA for Mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
        <Button
          asChild
          className="w-full bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold py-4 rounded-full"
        >
          <Link to="/booking?dropoff=Disneyland%20Park">
            Book Your Disneyland Transfer
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </Button>
      </div>
      <div className="lg:hidden h-20"></div> {/* Spacer for sticky CTA */}
    </div>
  );
};

export default DisneylandTransfers;
